import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { AnalyticsReportDefinition, AnalyticsShopSpecificFilters } from '../../../types'
import {
  cleanupInvalidFilterParams,
  getCurrentFilterValues,
  getFilterState,
  getFiltersForAPI,
  getFiltersForVisibility,
  updateFilterParam,
} from './utils/filterUtils'

type UseDynamicFiltersParamsParams = {
  reportDefinition: AnalyticsReportDefinition | undefined
  shopSpecificFilters: AnalyticsShopSpecificFilters | undefined
}

export const useDynamicFiltersParams = ({
  reportDefinition,
  shopSpecificFilters,
}: UseDynamicFiltersParamsParams) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const lastFilterGroupsRef = useRef<string>('')

  const filterGroups = useMemo(() => {
    if (!reportDefinition?.filterGroups) return []

    // Combine static filters from reportDefinition with dynamic filters from shopSpecificFilters
    return reportDefinition.filterGroups.map((group) => {
      if (group.filterType === 'dynamic' && shopSpecificFilters) {
        // For dynamic filters, use the shop-specific version
        const dynamicGroup = shopSpecificFilters.find(
          (dynamicGroup) => dynamicGroup.key === group.key,
        )

        return dynamicGroup || group
      }

      // For static filters, use the original group
      return group
    })
  }, [reportDefinition?.filterGroups, shopSpecificFilters])

  // Clean up invalid filter params from URL
  // Run when filterGroups change (e.g., when shopSpecificFilters load)
  // Also runs on initial mount if filterGroups are available
  // Use a ref to track filterGroups changes and avoid infinite loops
  useEffect(() => {
    if (filterGroups.length > 0) {
      const filterGroupsKey = JSON.stringify(
        filterGroups.map((g) => ({ key: g.key, filters: g.filters.map((f) => f.key) })),
      )

      // Clean up if filterGroups changed or on initial mount (when ref is empty)
      if (lastFilterGroupsRef.current !== filterGroupsKey) {
        lastFilterGroupsRef.current = filterGroupsKey
        cleanupInvalidFilterParams(filterGroups, searchParams, setSearchParams)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterGroups])

  const currentFilterValues = useMemo(() => {
    if (!filterGroups.length) return {}

    return getCurrentFilterValues(filterGroups, searchParams)
  }, [filterGroups, searchParams])

  const currentFilters = useMemo(() => {
    if (!filterGroups.length) return []

    return getFilterState(filterGroups, currentFilterValues)
  }, [filterGroups, currentFilterValues])

  const filtersForAPI = useMemo(() => {
    if (!filterGroups.length) return {}

    return getFiltersForAPI(currentFilterValues)
  }, [filterGroups.length, currentFilterValues])

  const hasActiveFilters = useMemo(() => {
    if (!filterGroups.length) return false

    return filterGroups.some((group) => {
      const values = currentFilterValues[group.key] || []

      if (group.selectionType === 'multiple') {
        return values.length > 0
      }

      // single selection: active if current differs from default
      const defaultFilterKeys = group.filters
        .filter((filter) => filter.default_value)
        .map((filter) => filter.key)

      const selectedValuesSet = new Set(values)
      const defaultValuesSet = new Set(defaultFilterKeys)
      if (selectedValuesSet.size !== defaultValuesSet.size) return true
      for (const value of selectedValuesSet) {
        if (!defaultValuesSet.has(value)) return true
      }

      return false
    })
  }, [filterGroups, currentFilterValues])

  const filtersForVisibility = useMemo(() => {
    if (!filterGroups.length) return {}

    return getFiltersForVisibility(filterGroups, currentFilterValues, hasActiveFilters)
  }, [filterGroups, currentFilterValues, hasActiveFilters])

  const hasFilters = useMemo(
    () => !!reportDefinition?.filterGroups,
    [reportDefinition?.filterGroups],
  )

  const handleUpdateFilterParam = useCallback(
    (groupKey: string, filterKey: string, checked: boolean, groupType?: 'single' | 'multiple') => {
      updateFilterParam(
        groupKey,
        filterKey,
        checked,
        groupType || 'multiple',
        searchParams,
        setSearchParams,
      )
    },
    [searchParams, setSearchParams],
  )

  const handleFilterChange = useCallback(
    (groupTitle: string, itemValue: string, checked: boolean) => {
      if (!filterGroups.length) return

      const group = filterGroups.find((g) => g.label === groupTitle)
      if (!group) return

      handleUpdateFilterParam(group.key, itemValue, checked, group.selectionType)
    },
    [filterGroups, handleUpdateFilterParam],
  )

  return {
    hasFilters,
    hasActiveFilters,
    currentFilters,
    filtersForAPI,
    filtersForVisibility,
    updateFilterParam: handleUpdateFilterParam,
    handleFilterChange,
  }
}
